/**
 * Admin — Access & roles.
 * Platform admins may toggle the `clinical_admin` flag on other admin users.
 * A clinical admin is the only role permitted to view/export cross-patient
 * clinical records. Every change is audited (role.change action).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldOff, KeyRound } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function AdminAccess() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const refresh = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/users-with-roles"); setUsers(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const toggle = async (u, next) => {
    setBusy({ ...busy, [u.id]: true });
    try {
      await api.patch(`/admin/users/${u.id}/clinical-admin`, { clinical_admin: next });
      toast.success(`${u.email} is now ${next ? "a clinical admin" : "no longer a clinical admin"}.`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy({ ...busy, [u.id]: false }); }
  };

  const resetMfa = async (u) => {
    if (!window.confirm(`Reset MFA for ${u.email}?\n\nTheir authenticator will be wiped and they'll be prompted to enrol again on next login. Use this when they've lost their device or enrolled in the wrong environment.`)) return;
    setBusy({ ...busy, [`mfa-${u.id}`]: true });
    try {
      await api.post(`/admin/users/${u.id}/reset-mfa`);
      toast.success(`MFA reset for ${u.email}`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
    finally { setBusy({ ...busy, [`mfa-${u.id}`]: false }); }
  };

  const admins = users.filter((u) => u.role === "admin");
  const clinicians = users.filter((u) => u.role === "clinician");

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        <div className="fl-label text-[#f47a20]">Access</div>
        <h1 className="fl-heading text-3xl md:text-4xl mt-2">Access & roles</h1>
        <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
          Platform admins can grant the <b>clinical_admin</b> flag to trusted admin accounts. Clinical admins are the only role permitted to view or export cross-patient clinical data. Ordinary platform admins do not have this access.
        </p>

        {/* Admins */}
        <section className="mt-8">
          <div className="fl-label text-[#f47a20] mb-2">Administrators</div>
          <div className="fl-card overflow-hidden" data-testid="access-admins-table">
            {loading && <div className="p-6 text-sm text-zinc-500">Loading…</div>}
            {!loading && admins.length === 0 && <div className="p-6 text-sm text-zinc-500">No admin users.</div>}
            {admins.map((u) => (
              <div key={u.id} className="p-4 border-b flex items-center gap-4" style={{ borderColor: "var(--fl-border)" }} data-testid={`access-admin-row-${u.id}`}>
                <div className="flex-1">
                  <div className="text-sm text-zinc-100">{u.name || u.email}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {u.email} · {u.mfa_enabled ? <span className="text-emerald-400">MFA on</span> : <span className="text-amber-400">MFA off</span>}
                    {u.active === false && <span className="text-red-400 ml-2">deactivated</span>}
                    {u.id === user?.id && <span className="text-blue-400 ml-2">(you)</span>}
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${u.clinical_admin ? "text-emerald-400 border-emerald-500/40" : "text-zinc-500 border-zinc-600/40"}`} data-testid={`access-flag-${u.id}`}>
                  {u.clinical_admin ? "clinical admin" : "platform admin"}
                </span>
                {u.id !== user?.id && (
                  <button
                    onClick={() => toggle(u, !u.clinical_admin)}
                    disabled={busy[u.id]}
                    className="fl-btn-ghost text-xs"
                    data-testid={`access-toggle-${u.id}`}
                  >
                    {busy[u.id] ? <Loader2 size={12} className="animate-spin"/> : u.clinical_admin ? <><ShieldOff size={12}/> Revoke</> : <><ShieldCheck size={12}/> Grant</>}
                  </button>
                )}
                <button
                  onClick={() => resetMfa(u)}
                  disabled={busy[`mfa-${u.id}`]}
                  className="fl-btn-ghost text-xs"
                  data-testid={`access-reset-mfa-${u.id}`}
                  title="Wipe MFA enrolment (device lost, wrong-env enrolment, etc.)"
                >
                  {busy[`mfa-${u.id}`] ? <Loader2 size={12} className="animate-spin"/> : <><KeyRound size={12}/> Reset MFA</>}
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Clinicians (read-only summary) */}
        <section className="mt-8">
          <div className="fl-label text-[#f47a20] mb-2">Clinicians ({clinicians.length})</div>
          <div className="fl-card overflow-hidden">
            {clinicians.map((u) => (
              <div key={u.id} className="p-4 border-b flex items-center gap-4 text-sm" style={{ borderColor: "var(--fl-border)" }} data-testid={`access-clin-row-${u.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-zinc-100 truncate">{u.name || u.email}</div>
                  <div className="text-xs text-zinc-500 truncate">{u.email} · {u.mfa_enabled ? <span className="text-emerald-400">MFA on</span> : <span className="text-amber-400">MFA off</span>}{u.active === false && <span className="text-red-400 ml-2">deactivated</span>}</div>
                </div>
                <button
                  onClick={() => resetMfa(u)}
                  disabled={busy[`mfa-${u.id}`]}
                  className="fl-btn-ghost text-xs shrink-0"
                  data-testid={`access-reset-mfa-${u.id}`}
                  title="Wipe MFA enrolment (device lost, wrong-env enrolment, etc.)"
                >
                  {busy[`mfa-${u.id}`] ? <Loader2 size={12} className="animate-spin"/> : <><KeyRound size={12}/> Reset MFA</>}
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}
